export type CellEnv = "dev" | "prod";

const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);
const TITLE_PREFIX = "DEV · ";
const COLOUR = "#c2185b";
export const STRIP_TEXT = "DEV · test cell, not the live app";

export function cellEnv(hostname: string): CellEnv {
  const host = hostname.toLowerCase();
  return host.startsWith("dev.") || LOCAL_HOSTS.has(host) ? "dev" : "prod";
}

export function devTitle(title: string): string {
  return title.startsWith(TITLE_PREFIX) ? title : TITLE_PREFIX + title;
}

const DEV_ICON =
  "data:image/svg+xml," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="12" fill="${COLOUR}"/>` +
      `<text x="32" y="41" font-family="system-ui,sans-serif" font-size="22" font-weight="700" fill="#fff" text-anchor="middle">DEV</text></svg>`,
  );

export const MANIFEST: Record<CellEnv, string> = { dev: "/manifest-dev.webmanifest", prod: "/manifest.webmanifest" };
export const TOUCH_ICON: Record<CellEnv, string> = { dev: "/icon-dev-180.png", prod: "/icon-180.png" };
export const APP_TITLE: Record<CellEnv, string> = { dev: "Xflow DEV", prod: "Xflow" };

/** Point the manifest, the touch icon and the iOS title at the dev app, so that an install gives Xflow DEV. */
export function linkDevApp(doc: Pick<ParentNode, "querySelectorAll">): number {
  let changed = 0;
  for (const [rel, href] of [["manifest", MANIFEST.dev], ["apple-touch-icon", TOUCH_ICON.dev]]) {
    for (const link of doc.querySelectorAll<HTMLLinkElement>(`link[rel="${rel}"]`)) {
      link.href = href;
      changed += 1;
    }
  }
  for (const meta of doc.querySelectorAll<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')) {
    meta.content = APP_TITLE.dev;
    changed += 1;
  }
  return changed;
}

export function installEnvMarker(hostname: string = location.hostname): CellEnv {
  const env = cellEnv(hostname);
  if (env !== "dev") return env;
  document.documentElement.dataset.env = "dev";
  console.info(`env: dev cell, ${linkDevApp(document)} app links and tags point to ${MANIFEST.dev}`);

  const strip = document.createElement("div");
  strip.className = "env-strip";
  strip.dataset.testid = "env-marker";
  strip.setAttribute("role", "region");
  strip.setAttribute("aria-label", "Test cell");
  strip.textContent = STRIP_TEXT;
  document.body.prepend(strip);

  for (const link of document.querySelectorAll<HTMLLinkElement>('link[rel="icon"]')) {
    link.type = "image/svg+xml";
    link.href = DEV_ICON;
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", COLOUR);

  const mark = () => {
    const title = devTitle(document.title);
    if (title !== document.title) document.title = title;
  };
  mark();
  new MutationObserver(mark).observe(document.head, { subtree: true, childList: true, characterData: true });
  return env;
}
